import { lazy, Suspense } from "react";
import {
  createRootRoute,
  createRoute,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { RootLayout } from "@/layout/RootLayout";
import { appShellPaths } from "./appShellPaths";

const AppShell = lazy(() => import("@/pages/app-shell"));
const InvitationAcceptPage = lazy(
  () => import("@/app/invitations/[token]/page"),
);
const ResetPasswordPage = lazy(
  () => import("@/app/reset-password/[token]/page"),
);
const VerifyEmailPage = lazy(() => import("@/app/verify-email/[token]/page"));
const OAuthCallbackPage = lazy(() => import("@/pages/oauth-callback-page"));
const OAuthErrorPage = lazy(() => import("@/components/oauth-error-notice"));
const CliAuthPage = lazy(() => import("@/pages/cli-auth-page"));
const LegacyScanRedirect = lazy(() =>
  import("@/components/legacy-scan-redirect").then((module) => ({
    default: module.LegacyScanRedirect,
  })),
);
const NotFound = lazy(() => import("@/app/not-found"));

/**
 * appShellPaths stays in `:param` form. It is the shared source of truth —
 * route-entry-coverage substitutes concrete values into it, and navigation.ts
 * matches against it — so the router adapts to that spelling rather than the
 * other way round.
 */
function toRoutePath(path: string) {
  return path.replace(/:(\w+)/g, "$$$1");
}

function Page({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

export function NotFoundBoundary() {
  return (
    <Page>
      <NotFound />
    </Page>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <RootLayout>
      <Outlet />
    </RootLayout>
  ),
});

/**
 * A pathless layout route, so the shell is mounted once and stays mounted
 * across every path below it.
 *
 * Giving each path its own shell component instead would remount the shell on
 * every navigation and reset the state it owns — which sections of the sidebar
 * are open, the quick-search query, whether the sidebar is collapsed. Those
 * survived under the previous router because all 86 paths rendered one shared
 * element; here the parent is what makes that true.
 */
const shellLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: () => (
    <Page>
      <AppShell />
    </Page>
  ),
});

// The shell reads the pathname and renders the page itself, so these carry no
// component of their own. Generated rather than written out so adding a
// navigation item cannot produce a link with no route behind it.
const shellRoutes = appShellPaths.map((path) =>
  createRoute({
    getParentRoute: () => shellLayoutRoute,
    path: toRoutePath(path),
    component: () => null,
  }),
);

/** Exported so tests assert against the real tree instead of a hand-copy. */
export const standaloneRoutePaths = [
  "/invitations/:token",
  "/reset-password/:token",
  "/verify-email/:token",
  "/auth/callback",
  "/auth/cli",
  "/oauth/error",
  "/dashboard",
  "/repositories/scans/:scanId",
] as const;

const standaloneComponents: Record<string, React.ComponentType> = {
  "/invitations/:token": InvitationAcceptPage,
  "/reset-password/:token": ResetPasswordPage,
  "/verify-email/:token": VerifyEmailPage,
  "/auth/callback": OAuthCallbackPage,
  "/auth/cli": CliAuthPage,
  "/oauth/error": OAuthErrorPage,
  "/repositories/scans/:scanId": LegacyScanRedirect,
};

const standaloneRoutes = standaloneRoutePaths
  .filter((path) => path !== "/dashboard")
  .map((path) => {
    const Component = standaloneComponents[path];
    return createRoute({
      getParentRoute: () => rootRoute,
      path: toRoutePath(path),
      component: () => (
        <Page>
          <Component />
        </Page>
      ),
    });
  });

// The retired dashboard route. A redirect thrown before load never renders a
// component, so the reader never sees a frame of the old destination.
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: () => {
    throw redirect({ to: "/", replace: true });
  },
});

// The scan page the Estate and Fleet pages replaced; scans start from a
// repository now.
const quickScanRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/quick-scan",
  beforeLoad: () => {
    throw redirect({ href: "/repositories", replace: true });
  },
});

const legacySettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  beforeLoad: ({ location }) => {
    const tab = new URLSearchParams(location.searchStr).get("tab");
    throw redirect({
      href: tab === "api-tokens" ? "/settings/api-tokens" : "/settings/account",
      replace: true,
    });
  },
});

export const routeTree = rootRoute.addChildren([
  legacySettingsRoute,
  shellLayoutRoute.addChildren(shellRoutes),
  ...standaloneRoutes,
  dashboardRoute,
  quickScanRoute,
]);
